import { signal, Signal, WritableSignal } from "@angular/core";
import { Entries } from "../store-types.model";
import { initMethodsStatus } from "../utils/store-utils.service";

/**
 * Service class for managing state in a store.
 *
 * @template U - The type of state managed by the store.
 */
export class StoreService<TState> {
  // State
  protected _state: { [key in keyof TState]: WritableSignal<TState[key]> };

  /**
   * Creates a new instance of StoreService with the provided initial state.
   *
   * This method is used to instantiate a store service for managing component state.
   *
   * @static
   * @param {TState} initialState - The initial state of the store service.
   * @returns {StoreService<TState>} An instance of StoreService initialized with the provided initial state.
   * @template TState - The type of state managed by the store service.
   */
  static createComponentStore<TState>(initialState: TState) {
    return new StoreService(initialState);
  }

  initStatus(type) {
    const statusState = initMethodsStatus(type.prototype, false);
    const state = Object.fromEntries(
      (Object.entries(statusState) as Entries<TState>[]).map(
        ([key, value]: [keyof TState, any]) => {
          return [key, signal(value)];
        },
      ),
    );
    this._state = { ...this._state, ...state };
  }

  /**
   * Constructs a new StoreService.
   *
   * @param {U} initialState - The initial state of the service.
   */
  constructor(initialState: TState) {
    this._initState(initialState);
  }

  /**
   * Retrieves the current state of the service.
   *
   * @public
   * @returns {{ [key in keyof U]: Signal<TState[key]> }} The current state of the service.
   */
  public getState(): { [key in keyof TState]: Signal<TState[key]> } {
    return this._state as { [key in keyof TState]: Signal<TState[key]> };
  }

  /**
   * Retrieves the current state of the service.
   *
   * @public
   * @returns {{ [key in keyof U]: Signal<WritableSignal[key]> }} The current state of the service.
   */
  public getWritableState(): {
    [key in keyof TState]: WritableSignal<TState[key]>;
  } {
    return this._state as {
      [key in keyof TState]: WritableSignal<TState[key]>;
    };
  }

  /**
   * Applies a partial state update to the service.
   *
   * @public
   * @param {Partial<TState>} statePart - The partial state to apply.
   */
  public patchState(statePart: Partial<TState> | Object): void {
    (Object.entries(statePart) as Entries<TState>).forEach(
      ([key, value]: [keyof TState, TState[keyof TState]]) => {
        if (this._state.hasOwnProperty(key)) {
          this._state[key].set(value);
        }
      },
    );
  }

  public updateStateKey<K extends keyof TState>(
    key: K,
    value: TState[K],
  ): void {
    this._state[key].set(value as TState[K]);
  }

  private _initState(initialState: TState) {
    this._state = Object.fromEntries(
      (Object.entries(initialState) as Entries<TState>[]).map(
        ([key, value]: [keyof TState, any]) => {
          return [key, signal(value)];
        },
      ),
    ) as { [key in keyof TState]: WritableSignal<TState[key]> };
  }
}
